import React from 'react';
import './TemplatesLibrary.scss';
import DefaultSubCatIcon from 'assets/icons/default-subcategories.svg';
import DefaultCatIcon from 'assets/icons/default-categories.svg';
import { Category } from 'interface/templated-experience/templated-experience.interface';

type TProps = {
  isAllTemplateSelected: boolean;
  onSidebarClick: (catId: string, subCatId: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  categoriesList: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectData: any;
  isError?: boolean;
  compact?: boolean;
};

const MODAL_SIDEBAR_TOP = [
  { key: '', name: 'All Templates' },
  { key: 'recently-added', name: 'Recently Added' },
];

const MODAL_SIDEBAR_BOTTOM = [
  { key: 'flybits-starters', name: 'Flybits Starters' },
  { key: 'byo', name: 'Custom Templates' },
];

// Keys already covered by the fixed modal groups above, plus the synthetic
// "unassigned" bucket. These are hidden from the custom categories list so we
// do not show duplicates.
const MODAL_RESERVED_CATEGORY_KEYS = [
  '',
  'all',
  'unassigned',
  'recently-added',
  'recently-used',
  'flybits-starters',
  'byo',
];

/**
 * Category navigation for the templates library. `compact` renders the modal's
 * fixed two-group list instead of the full category/subcategory tree.
 */
export const TemplatesLibrarySidebar = ({
  isAllTemplateSelected,
  onSidebarClick,
  categoriesList,
  projectData,
  isError = false,
  compact = false,
}: TProps) => {
  const DEFAULT_CLASS = 'templates-library__sidebar';
  /** Renders a subcategory button, skipping the synthetic `unassigned` bucket. */
  const getCategoryButton = (category: Category, subcategory: Category, index: number) => {
    if (subcategory.key && subcategory.key !== 'unassigned') {
      const subCategoryClass =
        projectData.cid === category.key && projectData.sid === subcategory.key ? ' selected' : '';
      const subCategoryImage = subcategory.iconUrl ? subcategory.iconUrl : DefaultSubCatIcon;
      return (
        <li key={index.toString()}>
          <button
            type="button"
            tabIndex={0}
            className={`${DEFAULT_CLASS}__button inner` + subCategoryClass}
            aria-label={subcategory.name ? subcategory.name : subcategory.key}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              if (event?.currentTarget?.className?.includes('selected')) {
                return;
              }
              onSidebarClick(category?.key || '', subcategory?.key || '');
            }}
          >
            <img className={`${DEFAULT_CLASS}__button__img`} src={`${subCategoryImage}`} alt={subcategory.key} />
            <span className={`${DEFAULT_CLASS}__button__label`}>
              {subcategory.name ? subcategory.name : subcategory.key}
            </span>
          </button>
        </li>
      );
    }
  };
  /** False when `unassigned` is the only subcategory, so the tree stays flat. */
  const isOnlyUnassignedSubCat = (subcategory: Category[]) => {
    let isUnassignedSubCat = true;
    if (subcategory.length === 1 && subcategory[0].key === 'unassigned') {
      isUnassignedSubCat = false;
    }
    return isUnassignedSubCat;
  };
  /**
   * Renders one entry of the modal's compact sidebar. An empty `key` means
   * "All Templates". Passing a `subKey` renders an indented subcategory, which
   * is selected only when both its category and subcategory are active.
   */
  const renderCompactItem = (key: string, name: string, subKey = '') => {
    const isSelected = subKey
      ? projectData.cid === key && projectData.sid === subKey
      : key
      ? projectData.cid === key && !projectData.sid
      : isAllTemplateSelected || isError;
    return (
      <button
        type="button"
        key={subKey ? `${key}-${subKey}` : key || 'all'}
        tabIndex={0}
        className={`${DEFAULT_CLASS}__button${subKey ? ` ${DEFAULT_CLASS}__button--sub` : ''}${
          isSelected ? ' selected' : ''
        }`}
        aria-label={name}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (event?.currentTarget?.className?.includes('selected')) {
            return;
          }
          onSidebarClick(key, subKey);
        }}
      >
        <span className={`${DEFAULT_CLASS}__button__label`}>{name}</span>
      </button>
    );
  };

  if (compact) {
    // Custom categories fetched from the tenant settings. We sort a copy so the
    // shared cached array from the query is never mutated, and drop the keys the
    // fixed groups above already cover.
    const customCategories: Category[] = (categoriesList || [])
      .filter((category: Category) => !MODAL_RESERVED_CATEGORY_KEYS.includes(category.key))
      .slice()
      .sort((firstCategory: Category, secondCategory: Category) =>
        (firstCategory.name || firstCategory.key) > (secondCategory.name || secondCategory.key) ? 1 : -1,
      );

    return (
      <div className={`${DEFAULT_CLASS} ${DEFAULT_CLASS}--compact`}>
        <nav aria-label="side panel">
          <div className={`${DEFAULT_CLASS}__group`}>
            {MODAL_SIDEBAR_TOP.map((item) => renderCompactItem(item.key, item.name))}
          </div>
          <div className={`${DEFAULT_CLASS}__divider`} />
          <div className={`${DEFAULT_CLASS}__group`}>
            {MODAL_SIDEBAR_BOTTOM.map((item) => renderCompactItem(item.key, item.name))}
          </div>
          {customCategories.length > 0 ? (
            <>
              <div className={`${DEFAULT_CLASS}__divider`} />
              <div className={`${DEFAULT_CLASS}__group`}>
                {customCategories.map((category: Category) => (
                  <React.Fragment key={category.key}>
                    {renderCompactItem(category.key, category.name || category.key)}
                    {category.subcategories
                      ?.filter((subcategory: Category) => subcategory.key && subcategory.key !== 'unassigned')
                      .slice()
                      .sort((firstSubcategory: Category, secondSubcategory: Category) =>
                        (firstSubcategory.name || firstSubcategory.key) >
                        (secondSubcategory.name || secondSubcategory.key)
                          ? 1
                          : -1,
                      )
                      .map((subcategory: Category) =>
                        renderCompactItem(category.key, subcategory.name || subcategory.key, subcategory.key),
                      )}
                  </React.Fragment>
                ))}
              </div>
            </>
          ) : null}
        </nav>
      </div>
    );
  }

  return (
    <div className={`${DEFAULT_CLASS}`}>
      <button
        type="button"
        tabIndex={0}
        className={`${DEFAULT_CLASS}__button` + (isAllTemplateSelected || isError ? ' selected' : '')}
        aria-label="All Templates"
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          if (event?.currentTarget?.className?.includes('selected')) {
            return;
          }
          onSidebarClick('', '');
        }}
      >
        <span className={`${DEFAULT_CLASS}__button__no-img`}></span>
        <span className={`${DEFAULT_CLASS}__button__label`}>All Templates</span>
      </button>
      {categoriesList?.length > 0 ? (
        <nav aria-label="side panel">
          {categoriesList
            ?.sort((firstCategory: Category, secondCategory: Category) =>
              firstCategory.name > secondCategory.name ? 1 : -1,
            )
            ?.map((_category: Category, olIndex: number) => {
              if (_category.key && _category.key !== 'all') {
                const categoryClass = projectData.cid === _category.key && !projectData.sid ? ' selected' : '';
                const categoryImage = _category.iconUrl || DefaultCatIcon;
                return (
                  <ol key={olIndex.toString()}>
                    <li>
                      <button
                        type="button"
                        tabIndex={0}
                        className={`${DEFAULT_CLASS}__button` + categoryClass}
                        aria-label={_category.name ? _category.name : _category.key}
                        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                          if (event?.currentTarget?.className?.includes('selected')) {
                            return;
                          }
                          onSidebarClick(_category?.key || '', '');
                        }}
                      >
                        <img className={`${DEFAULT_CLASS}__button__img`} src={`${categoryImage}`} alt={_category.key} />
                        <span className={`${DEFAULT_CLASS}__button__label`}>
                          {_category.name ? _category.name : _category.key}
                        </span>
                      </button>
                    </li>
                    {_category?.subcategories?.length && isOnlyUnassignedSubCat(_category.subcategories) ? (
                      <ol>
                        {_category?.subcategories
                          ?.sort((firstCategory: Category, secondCategory: Category) =>
                            firstCategory.name > secondCategory.name ? 1 : -1,
                          )
                          .map((subcategory: Category, index: number) => {
                            return getCategoryButton(_category, subcategory, index);
                          })}
                      </ol>
                    ) : (
                      ''
                    )}
                  </ol>
                );
              }
              return undefined;
            })}
        </nav>
      ) : (
        ''
      )}
    </div>
  );
};
